import type { AmAuditAnswer } from './types';

/**
 * Scoring an audit.
 *
 * The security templates (the QBR 2 sets, the CIS implementation groups) ask
 * yes/no/partial questions, and the answer to those is a control status rather
 * than a comment: in place, partially in place, or a gap. That is what makes a
 * percentage score and a risk-acceptance form possible, and it is why this
 * lives apart from the generic audit view.
 *
 * The naming here is deliberately "status", not red/amber/green. The Brand
 * Direct palette has no green or amber, so the LABELS are what the UI shows and
 * the colour is navy-to-crimson. The concept survives; the traffic light does
 * not.
 */
export type ControlStatus = 'in-place' | 'partial' | 'gap' | 'not-applicable';

export function controlStatus(response: string | null | undefined): ControlStatus {
  switch ((response ?? '').trim()) {
    case 'Yes':
      return 'in-place';
    case 'Partial':
      return 'partial';
    case 'No':
      return 'gap';
    default:
      /* N/A, Unknown and unanswered all land here, and all are excluded from
         the score — "we do not know" and a question nobody answered are not
         evidence of a control either way (migration 141). */
      return 'not-applicable';
  }
}

export const STATUS_LABELS: Record<ControlStatus, string> = {
  'in-place': 'In place',
  partial: 'Partially in place',
  gap: 'Gap',
  'not-applicable': 'N/A, unknown or unanswered',
};

export const STATUS_BADGES: Record<ControlStatus, string> = {
  'in-place': 'badge-good',
  partial: 'badge-warning',
  gap: 'badge-critical',
  'not-applicable': 'badge-neutral',
};

/** Whether a question set scores as a control review rather than a survey. */
export function isSecuritySet(setName: string | null | undefined): boolean {
  return /security|qbr\s*2|cis/i.test(setName ?? '');
}

const RISK_LEVELS = ['Critical', 'High', 'Medium', 'Low'];

/**
 * The risk level to put on a gap in the risk-acceptance form.
 *
 * The auditor's own rating wins. Failing that, several imported templates bake
 * a researched rating into the question's guidance as "Risk: High" — using it
 * is better than guessing. Only if neither exists is it derived from the
 * answer, and a flat "No" is treated as High because an absent control is
 * worse than a partial one.
 */
export function gapRisk(item: Pick<AmAuditAnswer, 'Risk' | 'Guidance' | 'Response'>): string {
  if (item.Risk) return item.Risk;
  const match = /(?:^|·)\s*Risk:\s*([^·]+)/.exec(item.Guidance ?? '');
  const found = match?.[1]?.trim();
  if (found && RISK_LEVELS.includes(found)) return found;
  return item.Response === 'No' ? 'High' : 'Medium';
}

const MATURITY_TIERS = ['Minimum', 'Standard', 'Advanced', 'Recommended'];

/** Maturity tier of an audit item. Imported security templates carry it either
 *  as a "Maturity: X" token in guidance (when grouped by domain) or as the
 *  category itself (when grouped by tier). */
export function itemMaturity(item: Pick<AmAuditAnswer, 'Guidance' | 'Category'>): string {
  const match = /(?:^|·)\s*Maturity:\s*([^·]+)/.exec(item.Guidance ?? '');
  const found = match?.[1]?.trim();
  if (found) return found;
  const category = (item.Category ?? '').trim();
  return MATURITY_TIERS.includes(category) ? category : '';
}

/** Guidance with the Maturity and Risk tokens removed, for when those have
 *  their own columns and repeating them is just noise. */
export function guidanceWithoutTokens(guidance: string | null | undefined): string {
  return (guidance ?? '')
    .split(' · ')
    .filter((part) => !/^\s*(Maturity|Risk):/.test(part))
    .join(' · ');
}

export interface AuditScore {
  counts: Record<ControlStatus, number>;
  /** How many answers counted toward the score. Zero means "not scoreable". */
  scored: number;
  /** 0-100. Partial credit is half, because half a control is not none. */
  score: number;
}

export function scoreAudit(
  items: Pick<AmAuditAnswer, 'Response'>[],
): AuditScore {
  const counts: Record<ControlStatus, number> = {
    'in-place': 0,
    partial: 0,
    gap: 0,
    'not-applicable': 0,
  };
  for (const item of items) counts[controlStatus(item.Response)]++;
  const scored = counts['in-place'] + counts.partial + counts.gap;
  const score = scored
    ? Math.round(((counts['in-place'] + counts.partial * 0.5) / scored) * 100)
    : 0;
  return { counts, scored, score };
}

/** Everything that is a gap or only partly there — the list a client is asked
 *  to either fix or formally accept. */
export const gapsOf = <T extends Pick<AmAuditAnswer, 'Response'>>(items: T[]): T[] =>
  items.filter((i) => {
    const status = controlStatus(i.Response);
    return status === 'gap' || status === 'partial';
  });
